import React from 'react';
import styled, { css, keyframes } from 'styled-components';

// #72 (UI düzeltme demeti T6 — "uygulama geneli buton/input modernizasyonu")
// — T2'nin (`pages/evaluation-periods/PeriodWizard.js`, task-4-brief bkz.
// t2-report.md §4) yerel `PrimaryCta`/`SecondaryCta` tarifi buraya
// GENELLEŞTİRİLDİ; T3 (`FeedbackFormsTab.js`'in `AddQuestionButton`'ı) ve T4
// (`MyGoalsPage.js`'in `BulkSubmitCta`'sı) AYNI tarifi birebir kopyalamıştı —
// üçü de bu görevde kaldırılıp doğrudan bu bileşene bağlandı (bkz. t6-report.md).
//
// Prop API'si GERİYE UYUMLU: `variant`/`fullWidth`/tüm native `<button>`
// prop'ları (onClick, disabled, type, ...) AYNEN duruyor — uygulamadaki
// YÜZLERCE mevcut `<Button>` çağrı sitesi hiçbir kod değişikliği olmadan
// yeni (kibar/minimal) görünümü otomatik alır.
//
// "primary" artık `components/layout/SidebarMenu.js`'in yerleşik gradyan
// kimliğini taşıyor (135deg primary→secondary + yumuşak gölge — "menü
// ikonları gradyandır" konvansiyonunun BİRİNCİ kez bir metin CTA'sına
// taşınışı, T2'de başlamıştı). "outline" artık T2'nin `SecondaryCta`'sıyla
// AYNI (ince kenarlık + yumuşak dolgu-hover — eski "hover'da tam dolguya
// dönüşme" davranışı kaldırıldı, o da "devasa" hissi güçlendiren bir
// etkiydi). "ghost" ZATEN kibardı, dokunulmadı. "secondary" (turkuaz düz
// dolgu) marka rengini KORUYOR, yalnız taban dolgu/köşe yarıçapından (aşağı)
// faydalanıyor.
const variants = {
  primary: css`
    background: linear-gradient(135deg, ${({ theme }) => theme.colors.primary.main}, ${({ theme }) => theme.colors.secondary.main});
    color: #fff;
    box-shadow: 0 2px 8px rgba(2, 145, 239, 0.28);

    &:hover:not(:disabled) { box-shadow: 0 4px 14px rgba(2, 145, 239, 0.38); }
    &:disabled { box-shadow: none; }
  `,
  secondary: css`
    background: ${({ theme }) => theme.colors.secondary.main};
    color: #fff;
    &:hover:not(:disabled) { background: ${({ theme }) => theme.colors.secondary.dark}; }
  `,
  outline: css`
    background: transparent;
    color: ${({ theme }) => theme.colors.primary.main};
    border: 1.5px solid ${({ theme }) => theme.colors.primary.main};
    &:hover:not(:disabled) { background: rgba(2, 145, 239, 0.08); }
  `,
  ghost: css`
    background: transparent;
    color: ${({ theme }) => theme.colors.primary.main};
    &:hover:not(:disabled) { background: rgba(2, 145, 239, 0.08); }
  `,
  // #72 — T2'nin `PrimaryCta`sının "danger" ihtiyacı yoktu ama sil/iptal gibi
  // yıkıcı birincil aksiyonlar için uygulamada dağınık `styled(Button)` + hex
  // override'ları vardı (bkz. t6-report.md envanteri) — tek yerden.
  danger: css`
    background: ${({ theme }) => theme.colors.auxiliar.danger};
    color: #fff;
    &:hover:not(:disabled) { background: #C62828; }
  `,
};

const StyledButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 20px;
  border: none;
  border-radius: ${({ theme }) => theme.metrics.radius.xl};
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  cursor: pointer;
  width: ${({ $fullWidth }) => ($fullWidth ? '100%' : 'auto')};

  @media (prefers-reduced-motion: no-preference) {
    transition: box-shadow 0.2s ease, background 0.2s ease, opacity 0.2s ease, border-color 0.2s ease;
  }

  ${({ $variant }) => variants[$variant] || variants.primary}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

// FE-29 — `loading` prop'u 56 çağrı yerinde ZATEN geçiliyordu ama bileşen onu
// TÜKETMİYORDU: React bilinmeyen prop'u DOM'a yazıyor (`<button loading="true">`)
// ve hiçbir görsel geri bildirim çıkmıyordu. Kullanıcı "Gönder"e bastıktan sonra
// istek uçarken butonun durumu değişmiyor, ikinci kez basıyor.
//
// Çocuklar DEĞİŞTİRİLMEZ: çağrı yerlerinin çoğu `{loading ? '...' : metin}`
// yazıyor ve o metinler testlerde iddia ediliyor. Spinner metnin SOLUNA eklenir.
const Spinner = styled.span`
  width: 14px;
  height: 14px;
  flex: 0 0 auto;
  border-radius: 50%;
  border: 2px solid currentColor;
  border-top-color: transparent;
  opacity: 0.85;

  @media (prefers-reduced-motion: no-preference) {
    animation: ${spin} 0.7s linear infinite;
  }
`;

export default function Button({
  children, variant = 'primary', fullWidth = false, loading = false, disabled = false, ...props
}) {
  return (
    <StyledButton
      $variant={variant}
      $fullWidth={fullWidth}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...props}
    >
      {loading && <Spinner aria-hidden="true" data-testid="button-spinner" />}
      {children}
    </StyledButton>
  );
}
